import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { TrendingUp, Users, Activity, AlertCircle, Wallet, Printer, Cloud, CheckCircle2, Building2, Star } from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  BarChart, Bar,
} from 'recharts';

export default function AdminKPIs() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [savedToCloud, setSavedToCloud] = useState(false);

  const load = (d = days) => api.get('/admin/kpis', { params: { days: d } }).then(r => setData(r.data));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const printPage = () => window.print();
  const saveToCloud = async () => {
    // Trigger an on-demand backup which includes settings + KPI snapshots.
    try {
      await api.post('/admin/backup/backblaze');
      setSavedToCloud(true); setTimeout(()=>setSavedToCloud(false), 3000);
    } catch (e) {
      alert('Cloud save failed: ' + (e?.response?.data?.detail || e.message) + '. Configure Backblaze B2 in Settings.');
    }
  };

  if (!data) return <div className="p-8 text-fg-muted">Loading KPIs…</div>;

  const revText = Object.entries(data.revenue || {}).map(([c, v]) => `${v.toFixed(0)} ${c}`).join(' · ') || '—';

  return (
    <div className="p-8 max-w-6xl" data-testid="admin-kpis-page">
      <style>{`@media print { .no-print { display: none !important; } .bento-card { break-inside: avoid; } }`}</style>
      <div className="flex items-end justify-between mb-2 flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-brand font-semibold mb-2">PERFORMANCE</div>
          <h1 className="text-4xl font-heading font-bold tracking-tight">Key Performance Indicator</h1>
        </div>
        <div className="flex flex-wrap gap-2 no-print">
          <select className="input max-w-[150px]" value={days}
                  onChange={e=>{ const v=parseInt(e.target.value); setDays(v); load(v); }}
                  data-testid="kpis-range">
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="365">Last year</option>
          </select>
          <button onClick={printPage} className="btn-secondary text-sm inline-flex items-center gap-2" data-testid="kpis-print">
            <Printer className="w-4 h-4"/> Print
          </button>
          <button onClick={saveToCloud} className="btn-secondary text-sm inline-flex items-center gap-2" data-testid="kpis-cloud">
            {savedToCloud ? <CheckCircle2 className="w-4 h-4 text-brand"/> : <Cloud className="w-4 h-4"/>}
            {savedToCloud ? 'Saved' : 'Save to cloud'}
          </button>
        </div>
      </div>

      <h2 className="text-base text-fg-muted mb-6 font-heading">Analytics</h2>

      <div className="grid md:grid-cols-4 gap-3 mb-6">
        <Stat icon={Wallet} label="Revenue" v={revText} testid="kpi-revenue"/>
        <Stat icon={Activity} label="Completed" v={data.appointments.completed} sub={`${data.appointments.scheduled} upcoming`} testid="kpi-completed"/>
        <Stat icon={AlertCircle} label="No-show rate" v={`${data.no_show_pct}%`} sub={`${data.appointments.no_show} no-shows`} testid="kpi-noshow"/>
        <Stat icon={Users} label="Retention" v={`${data.retention.retention_pct}%`} sub={`${data.retention.returning_patients}/${data.retention.active_patients} returning`} testid="kpi-retention"/>
      </div>

      {data.daily_series && data.daily_series.length > 0 && (
        <>
          <div className="bento-card mb-6" data-testid="kpis-trend-chart">
            <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-brand"/> Daily trend
            </h3>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                <LineChart data={data.daily_series}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E0"/>
                  <XAxis dataKey="date" tick={{ fontSize: 11 }}
                         tickFormatter={d => d.slice(5)}/>
                  <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 12 }}/>
                  <Line yAxisId="left" type="monotone" dataKey="completed" stroke="#003D80" strokeWidth={2} dot={false} name="Completed"/>
                  <Line yAxisId="left" type="monotone" dataKey="no_show" stroke="#B83A3A" strokeWidth={2} dot={false} name="No-show"/>
                  <Line yAxisId="right" type="monotone" dataKey="revenue" stroke="#2A5C4E" strokeWidth={2} dot={false} name="Revenue"/>
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bento-card mb-6" data-testid="kpis-newpatients-chart">
            <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
              <Users className="w-4 h-4 text-brand"/> New patient acquisition
            </h3>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                <BarChart data={data.daily_series}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E0"/>
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={d => d.slice(5)}/>
                  <YAxis tick={{ fontSize: 11 }}/>
                  <Tooltip />
                  <Bar dataKey="new_patients" fill="#B8861F" name="New patients"/>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}

      <div className="bento-card">
        <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-brand"/> Therapist productivity
        </h3>
        {data.utilization.length === 0 ? (
          <div className="text-fg-muted text-sm">No bookings in this window.</div>
        ) : (
          <table data-testid="utilization-table">
            <thead><tr><th>Therapist</th><th>Hours booked</th><th>Utilization</th></tr></thead>
            <tbody>
              {data.utilization.map(r => (
                <tr key={r.clinician_id}>
                  <td>{r.clinician_name}</td>
                  <td className="font-mono text-xs">{r.hours_booked} h</td>
                  <td>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-soft rounded-full h-2 overflow-hidden max-w-[200px]">
                        <div className="h-full bg-brand transition-all" style={{ width: `${r.utilization_pct}%` }}/>
                      </div>
                      <span className="text-xs font-medium">{r.utilization_pct}%</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="text-[10px] text-fg-muted mt-3">Utilization is computed against a baseline of 30 working hours per week per clinician.</div>
      </div>

      {/* Per-branch breakdown — only meaningful when the tenant has ≥1 branch
          with bookings. Single-location tenants see one "Unassigned" row which
          we hide via the conditional. */}
      {(data.by_location || []).length > 0 && (data.by_location.length > 1 || data.by_location[0]?.location_id) && (
        <div className="bento-card" data-testid="branch-kpis">
          <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
            <Building2 className="w-4 h-4 text-brand"/> Per-branch breakdown
          </h3>
          <table>
            <thead>
              <tr>
                <th>Branch</th>
                <th className="text-right">Bookings</th>
                <th className="text-right">Completed</th>
                <th className="text-right">Cancelled</th>
                <th className="text-right">No-show</th>
                <th className="text-right">Revenue</th>
                <th className="text-right">Avg ★</th>
              </tr>
            </thead>
            <tbody>
              {data.by_location.map((r, idx) => (
                <tr key={r.location_id || `_un-${idx}`} data-testid={`branch-row-${r.location_id || 'unassigned'}`}>
                  <td>
                    <div className="flex items-center gap-1.5">
                      {r.location_id ? <Building2 className="w-3.5 h-3.5 text-brand"/> : <AlertCircle className="w-3.5 h-3.5 text-fg-muted"/>}
                      <span className={r.location_id ? '' : 'text-fg-muted italic'}>{r.location_name}</span>
                    </div>
                  </td>
                  <td className="text-right font-mono text-xs">{r.total_bookings}</td>
                  <td className="text-right font-mono text-xs text-[#1B5E20]">{r.completed}</td>
                  <td className="text-right font-mono text-xs text-[#B83A3A]">{r.cancelled}</td>
                  <td className="text-right font-mono text-xs text-[#B8860B]">{r.no_show}</td>
                  <td className="text-right font-mono text-xs">
                    {Object.keys(r.revenue || {}).length === 0
                      ? <span className="text-fg-muted">—</span>
                      : Object.entries(r.revenue).map(([cur, v]) => `${v.toFixed(0)} ${cur}`).join(' · ')}
                  </td>
                  <td className="text-right font-mono text-xs">
                    {r.avg_rating !== null && r.avg_rating !== undefined
                      ? <span className="inline-flex items-center gap-0.5 justify-end"><Star className="w-3 h-3 fill-[#FFB000] text-[#FFB000]"/> {r.avg_rating.toFixed(1)} <span className="text-fg-muted">({r.rating_count})</span></span>
                      : <span className="text-fg-muted">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="text-[10px] text-fg-muted mt-3">
            "Unassigned" rows are bookings made before the branch picker was wired up — they don't count against any specific location.
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, v, sub, testid }) {
  return (
    <div className="bento-card" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-fg-muted mb-2">
        <Icon className="w-3.5 h-3.5 text-brand"/> {label}
      </div>
      <div className="text-3xl font-heading font-bold text-brand truncate">{v}</div>
      {sub && <div className="text-xs text-fg-muted mt-1">{sub}</div>}
    </div>
  );
}
